/**
 * 刪除客戶確認對話框。
 * 使用者必須手動輸入客戶編號(cCode)才能啟用「確認刪除」。
 * 若該客戶沒有 cCode(可空),改為要求輸入「簡稱」做確認。
 */
import { useState } from 'react';
import { Modal } from '../../components/Modal';

export interface DeleteCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  simpleName: string;
  cCode: string | null;
  submitting?: boolean;
  errorMessage?: string | null;
}

export function DeleteCustomerDialog({
  open,
  onClose,
  onConfirm,
  simpleName,
  cCode,
  submitting,
  errorMessage,
}: DeleteCustomerDialogProps): JSX.Element {
  const [typed, setTyped] = useState('');
  // cCode 優先;無則以簡稱作為防誤刪確認字串
  const expected = cCode ?? simpleName;
  const confirmLabel = cCode ? '客戶編號' : '簡稱';
  const matches = typed.trim() === expected;

  const close = (): void => {
    if (submitting) return;
    setTyped('');
    onClose();
  };

  const handleConfirm = (): void => {
    if (!matches || submitting) return;
    onConfirm();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="確認刪除客戶"
      dismissOnBackdrop={!submitting}
      widthClass="max-w-lg"
    >
      <div className="grid gap-3">
        <div className="text-sm text-slate-700">
          這個動作將軟刪除客戶{' '}
          <span className="font-semibold">{simpleName}</span>
          {cCode ? (
            <>
              {' '}
              <span className="font-mono text-slate-500">({cCode})</span>
            </>
          ) : null}
          ,與其訪談紀錄會從列表隱藏。
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
          為避免誤刪,請在下方輸入{confirmLabel}{' '}
          <span className="font-mono font-semibold">{expected}</span> 才能啟用刪除。
        </div>
        <label className="grid gap-1">
          <span className="text-sm text-slate-700">輸入{confirmLabel}以確認</span>
          <input
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="w-full border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-red-400 font-mono"
            placeholder={expected}
            autoFocus
            data-testid="delete-confirm-input"
          />
        </label>
        {errorMessage ? (
          <div role="alert" className="bg-red-50 text-red-700 text-sm p-2 rounded">
            {errorMessage}
          </div>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={close}
            disabled={submitting}
            className="border rounded px-3 py-1.5 hover:bg-slate-100 disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!matches || submitting}
            className="bg-red-600 text-white rounded px-3 py-1.5 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="delete-confirm-btn"
          >
            {submitting ? '刪除中…' : '確認刪除'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
